import { cook, precookFunction, __dev_only_clearGlobalExecutionContextStack, __dev_only_getGlobalExecutionContextStack } from "@next-core/cook";
import { supply } from "@next-core/supply";
import { collectMemberUsageInFunction } from "@next-core/utils/storyboard";
import { hasOwnProperty } from "@next-core/utils/general";
import { getGeneralGlobals } from "./internal/compute/getGeneralGlobals.js";

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */

/** @internal */
export function StoryboardFunctionRegistryFactory() {
  let {
    widgetId,
    widgetVersion,
    isolatedRoot,
    collectCoverage,
    debuggerOverrides
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const registeredFunctions = new Map();
  const overrides = debuggerOverrides === null || debuggerOverrides === void 0 ? void 0 : debuggerOverrides({
    precookFunction,
    cook,
    supply
  });

  // Use `Proxy` with a frozen target, to make a readonly function registry.
  const storyboardFunctions = new Proxy(Object.freeze({}), {
    get(_target, key) {
      return getStoryboardFunction(key);
    }
  });
  let currentApp;
  function registerStoryboardFunctions(functions, app) {
    if (app) {
      currentApp = app;
    }
    registeredFunctions.clear();
    if (Array.isArray(functions)) {
      for (const fn of functions) {
        var _fn$perm;
        let deps = fn.deps;
        if (deps == null) {
          deps = collectMemberUsageInFunction(fn, "FN", !!collectCoverage);
          deps.delete(fn.name);
        }
        const hasPermissionsCheck = (_fn$perm = fn.perm) !== null && _fn$perm !== void 0 ? _fn$perm : collectMemberUsageInFunction(fn, "PERMISSIONS", !!collectCoverage).has("check");
        registeredFunctions.set(fn.name, {
          source: fn.source,
          typescript: fn.typescript,
          transformed: fn.transformed,
          deps,
          hasPermissionsCheck
        });
      }
    }
  }
  function getGlobalVariables(globals) {
    return supply(globals, getGeneralGlobals(globals, {
      collectCoverage,
      widgetId,
      widgetVersion,
      isolatedRoot,
      app: currentApp,
      storyboardFunctions,
      isStoryboardFunction: true
    }), !!collectCoverage);
  }
  function getStoryboardFunction(name) {
    const fn = registeredFunctions.get(name);
    if (!fn) {
      return undefined;
    }
    if (fn.processed) {
      return fn.cooked;
    }
    let collector;
    if (collectCoverage) {
      collector = collectCoverage.createCollector(name);
    }

    // Do not use transformed functions when debugging.
    const transformed = !overrides && !collector && fn.transformed;
    if (transformed) {
      const globalVariables = getGlobalVariables(transformed.globals);
      // Spread globals as params to prevent accessing forbidden globals.
      // NOTE: in native mode, forbidden globals are declared as `undefined`,
      // thus accessing them will not throw a ReferenceError.
      fn.cooked = new Function(...transformed.globals, `"use strict";return (${transformed.source})`)(...transformed.globals.map(key => hasOwnProperty(globalVariables, key) ? globalVariables[key] : undefined));
    } else {
      const precooked = precookFunction(fn.source, {
        typescript: fn.typescript,
        hooks: collector && {
          beforeVisit: collector.beforeVisit
        },
        cacheKey: fn
      });
      const globalVariables = getGlobalVariables(precooked.attemptToVisitGlobals);
      fn.cooked = cook(precooked.function, fn.source, {
        rules: {
          noVar: true
        },
        globalVariables: overrides ? {
          ...globalVariables,
          ...(overrides !== null && overrides !== void 0 && overrides.LodashWithStaticFields && precooked.attemptToVisitGlobals.has("_") ? {
            _: {
              ...globalVariables._,
              ...overrides.LodashWithStaticFields
            }
          } : null),
          ...(overrides !== null && overrides !== void 0 && overrides.ArrayConstructor && precooked.attemptToVisitGlobals.has("Array") ? {
            Array: overrides.ArrayConstructor
          } : null),
          ...(overrides !== null && overrides !== void 0 && overrides.ObjectWithStaticFields && precooked.attemptToVisitGlobals.has("Object") ? {
            Object: {
              ...globalVariables.Object,
              ...overrides.ObjectWithStaticFields
            }
          } : null)
        } : globalVariables,
        ArrayConstructor: overrides === null || overrides === void 0 ? void 0 : overrides.ArrayConstructor,
        hooks: collector && {
          beforeEvaluate: collector.beforeEvaluate,
          beforeCall: collector.beforeCall,
          beforeBranch: collector.beforeBranch
        },
        debug: !!debuggerOverrides
      });
    }
    fn.processed = true;
    return fn.cooked;
  }
  return {
    storyboardFunctions,
    registerStoryboardFunctions,
    updateStoryboardFunction(name, data) {
      const newFn = {
        ...data,
        name
      };
      const deps = collectMemberUsageInFunction(newFn, "FN", true);
      const hasPermissionsCheck = collectMemberUsageInFunction(newFn, "PERMISSIONS", true).has("check");
      registeredFunctions.set(name, {
        source: data.source,
        typescript: data.typescript,
        transformed: data.transformed,
        deps,
        hasPermissionsCheck
      });
    },
    /**
     * Check whether listed functions attempt to call `PERMISSIONS.check()`,
     * includes in nested `FN.*()` calls.
     */
    checkPermissionsUsage(functionNames) {
      const checkedFunctions = new Set();
      const hasPermissionsCheck = name => {
        if (!checkedFunctions.has(name)) {
          checkedFunctions.add(name);
          const fn = registeredFunctions.get(name);
          return !!fn && (fn.hasPermissionsCheck || [...fn.deps].some(hasPermissionsCheck));
        }
        return false;
      };
      return functionNames.some(hasPermissionsCheck);
    },
    clearGlobalExecutionContextStack() {
      __dev_only_clearGlobalExecutionContextStack();
    },
    getGlobalExecutionContextStack() {
      return __dev_only_getGlobalExecutionContextStack();
    }
  };
}
//# sourceMappingURL=StoryboardFunctionRegistry.js.map